import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { Sparkles, Trash2, ExternalLink, Loader2 } from "lucide-react";

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [topic, setTopic] = useState("");
  const [theme, setTheme] = useState("general");
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/blog?limit=50");
    setPosts(data.posts);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true); setError(null);
    try {
      const body = topic ? { topic, theme } : {};
      await api.post("/admin/blog/generate", body, { withCredentials: true });
      setTopic("");
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const del = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${slug}`, { withCredentials: true });
    load();
  };

  return (
    <section data-testid="blog-manager">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl text-white">BreezeBlog</h2>
        <div className="text-white/40 text-xs">
          Auto-generated daily at 9:00 UTC · {posts.length} posts
        </div>
      </div>

      <div className="rounded-xl border border-[#FF3D8B]/30 bg-gradient-to-br from-[#FF3D8B]/5 to-[#A855F7]/5 p-5 mb-6">
        <div className="text-white/60 text-xs uppercase tracking-widest mb-3 flex items-center gap-2">
          <Sparkles size={14} className="text-[#FF3D8B]" /> Have Bree write a new post
        </div>
        <div className="flex flex-wrap gap-3">
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Topic (leave blank for rotation pick)"
            data-testid="blog-topic-input"
            className="flex-1 min-w-[240px] bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF3D8B]"
          />
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            data-testid="blog-theme-select"
            className="bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF3D8B]"
          >
            <option value="general">General</option>
            <option value="models">Models</option>
            <option value="car accessories">Car Accessories</option>
            <option value="fragrance">Fragrance</option>
            <option value="tech">Tech</option>
            <option value="lifestyle">Lifestyle</option>
          </select>
          <button
            onClick={generate}
            disabled={generating}
            data-testid="blog-generate-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] disabled:opacity-40 transition-colors"
          >
            {generating ? <><Loader2 size={14} className="animate-spin" /> Writing…</> : <>Generate Now</>}
          </button>
        </div>
        {error && <div className="text-red-400 text-xs mt-3">{error}</div>}
        <div className="text-white/40 text-[11px] mt-3">
          Bree will search the live web via Tavily, write a 700–1000 word SEO-optimized post, generate a hero image,
          and publish it — usually takes 30–90 seconds.
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 text-sm">Loading posts…</div>
      ) : posts.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-8 text-center text-white/40 text-sm">
          No posts yet. Click "Generate Now" to publish your first one.
        </div>
      ) : (
        <ul className="space-y-3">
          {posts.map((p) => (
            <li key={p.slug} className="flex items-center gap-4 p-4 rounded-lg border border-white/10 bg-[#0f0f0f]" data-testid={`blog-row-${p.slug}`}>
              {p.image_url && (
                <img src={resolveImage(p.image_url)} alt={p.title}
                  className="w-20 h-14 rounded object-cover bg-white/5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold truncate">{p.title}</div>
                <div className="text-white/40 text-xs mt-1">
                  {(p.published_at || "").slice(0, 10)} · {(p.tags || []).slice(0, 3).join(" · ")}
                </div>
              </div>
              <Link
                to={`/blog/${p.slug}`}
                target="_blank"
                className="text-white/60 hover:text-[#A855F7] p-2"
                title="Open post"
              >
                <ExternalLink size={16} />
              </Link>
              <button
                onClick={() => del(p.slug)}
                data-testid={`blog-delete-${p.slug}`}
                className="text-white/40 hover:text-red-400 p-2"
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
